import React from "react";
import { Avatar, Box, Grid, IconButton, Rating } from "@mui/material";
import { Delete } from "@mui/icons-material";
import { red } from "@mui/material/colors";
import { Review } from "../../../types/reviewTypes";
import { useAppDispatch } from "../../../State/Store";
import { deleteProductReview } from "../../../State/customer/reviewSlice";

interface ReviewCardProps {
  review?: Review;
  currentUserId?: number;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, currentUserId }) => {
  const dispatch = useAppDispatch();

  if (!review) return null;

  const reviewerName = review.user?.firstName
    ? `${review.user.firstName} ${review.user.lastName || ""}`.trim()
    : "Verified Customer";
  const avatarLetter = review.user?.firstName ? review.user.firstName.charAt(0).toUpperCase() : "U";

  const reviewDate = review.createdAt
    ? new Date(review.createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

  const handleDelete = () => {
    const jwt = localStorage.getItem("jwt");
    if (jwt && review.id) {
      dispatch(deleteProductReview({ reviewId: review.id, jwt }));
    }
  };

  const isOwner = Boolean(currentUserId && review.user?.id === currentUserId);
  const imagesToDisplay = review.productImages || review.reviewImages || [];

  return (
    <div className="p-4 border border-gray-100 rounded-lg shadow-sm bg-white mb-4 transition-all hover:shadow-md">
      <div className="flex justify-between items-start w-full">
        <Grid container spacing={2}>
          <Grid size={{ xs: 2, sm: 1 }}>
            <Avatar className="text-white font-bold" sx={{ width: 42, height: 42, bgcolor: "#0d9488" }}>
              {avatarLetter}
            </Avatar>
          </Grid>

          <Grid size={{ xs: 10, sm: 11 }}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between">
              <div>
                <p className="font-semibold text-sm sm:text-base text-gray-800">{reviewerName}</p>
                <p className="text-xs text-gray-400">{reviewDate}</p>
              </div>
            </div>

            <Box sx={{ my: 1 }}>
              <Rating readOnly value={review.rating || 0} precision={0.5} size="small" />
            </Box>

            <p className="text-gray-700 text-sm leading-relaxed mb-3">{review.reviewText}</p>

            {imagesToDisplay.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {imagesToDisplay.map((imgUrl: string, idx: number) => (
                  <img
                    key={idx}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md border border-gray-200"
                    src={imgUrl}
                    alt={`Review visual ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </Grid>
        </Grid>

        {isOwner && (
          <IconButton size="small" onClick={handleDelete} title="Delete Review">
            <Delete sx={{ color: red[600], fontSize: "20px" }} />
          </IconButton>
        )}
      </div>
    </div>
  );
};

export default ReviewCard;